import { BUTTON_BORDER_RADIUS_CSS, ENABLE_SHOPPER_BRAND_SETTINGS, FONT_SLOT_BY_CHOICE, fontFamilyBySlot, MEDIA_BORDER_RADIUS_CSS, } from '../api-types/customization.js';
import { computeCtaBorder } from './ctaBorder.js';
export const PRIMARY_FONT_CSS_VAR = '--primary-font';
export const SECONDARY_FONT_CSS_VAR = '--secondary-font';
export const FONT_3_CSS_VAR = '--font-3';
export const FONT_4_CSS_VAR = '--font-4';
export const FONT_5_CSS_VAR = '--font-5';
export const PRIMARY_CTA_TEXT_COLOR_VAR = '--primary-cta-text-color';
export const PRIMARY_CTA_FILL_COLOR_VAR = '--primary-cta-fill-color';
export const ACCENT_FILL_COLOR_VAR = '--accent-fill-color';
export const ACCENT_TEXT_COLOR_VAR = '--accent-text-color';
export const PRIMARY_PAGE_FILL_COLOR_VAR = '--primary-page-fill-color';
export const PRIMARY_PAGE_TEXT_COLOR_VAR = '--primary-page-text-color';
export const SECONDARY_CTA_TEXT_COLOR_VAR = '--secondary-cta-text-color';
export const SECONDARY_CTA_FILL_COLOR_VAR = '--secondary-cta-fill-color';
export const TERTIARY_CTA_TEXT_COLOR_VAR = '--tertiary-cta-text-color';
export const TERTIARY_CTA_FILL_COLOR_VAR = '--tertiary-cta-fill-color';
export const PRIMARY_DISCOUNT_COLOR_VAR = '--primary-discount-color';
export const SECONDARY_DISCOUNT_COLOR_VAR = '--secondary-discount-color';
export const PRIMARY_ICON_COLOR_VAR = '--primary-icon-color';
export const SUBTLE_BORDER_COLOR_VAR = '--subtle-border-color';
export const SUBTLE_PRIMARY_COLOR_VAR = '--subtle-primary-color';
export const HEADER_BORDER_COLOR_VAR = '--header-border-color';
export const PRIMARY_CTA_BORDER_VAR = '--primary-cta-border';
export const SECONDARY_CTA_BORDER_VAR = '--secondary-cta-border';
export const TERTIARY_CTA_BORDER_VAR = '--tertiary-cta-border';
export const BUTTON_BORDER_RADIUS_VAR = '--button-border-radius';
export const MEDIA_BORDER_RADIUS_VAR = '--media-border-radius';
export const FONT_FAMILY_DEFAULTS = '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif';
const TYPOGRAPHY_VAR_PREFIX = '--sf-typography';
/** The header hairline brands have always had; kept until a brand picks a border colour. */
const DEFAULT_HEADER_BORDER_COLOR = '#e5e7eb';
const TEXT_TRANSFORM_CSS_VALUES = {
    NONE: 'none',
    UPPERCASE: 'uppercase',
    LOWERCASE: 'lowercase',
    CAPITALIZE: 'capitalize',
};
const FONT_CSS_VAR_BY_SLOT = {
    primary: PRIMARY_FONT_CSS_VAR,
    secondary: SECONDARY_FONT_CSS_VAR,
    font3: FONT_3_CSS_VAR,
    font4: FONT_4_CSS_VAR,
    font5: FONT_5_CSS_VAR,
};
function camelToKebab(value) {
    return value.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}
/** `--sf-typography-<role>-<prop>`, the property a role-aware element reads. */
export function typographyCssVarName(role, prop) {
    return `${TYPOGRAPHY_VAR_PREFIX}-${camelToKebab(role)}-${prop}`;
}
/**
 * The `--sf-typography-<role>-<prop>` entries for the brand's configured
 * typography role overrides — five per role, none when nothing is
 * configured so every role-aware element falls back to its own styling.
 */
export function getTypographyCssVariableEntries(overrides) {
    const entries = [];
    for (const override of overrides) {
        const name = (prop) => typographyCssVarName(override.role, prop);
        entries.push([
            name('font-family'),
            `var(${FONT_CSS_VAR_BY_SLOT[FONT_SLOT_BY_CHOICE[override.fontFamily]]})`,
        ]);
        entries.push([name('font-weight'), String(override.fontWeight)]);
        entries.push([name('line-height'), String(override.lineHeight)]);
        entries.push([
            name('text-transform'),
            TEXT_TRANSFORM_CSS_VALUES[override.textTransform],
        ]);
        entries.push([name('letter-spacing'), override.letterSpacing]);
    }
    return entries;
}
export function buildTypographyCssVariables(overrides) {
    return getTypographyCssVariableEntries(overrides)
        .map(([name, value]) => `${name}: ${value};`)
        .join('\n');
}
function headerBorderColor(settings) {
    if (settings.headerBorderEnabled === false)
        return 'transparent';
    return settings.borderColorOverride || DEFAULT_HEADER_BORDER_COLOR;
}
/** Font slots 3–5 fall back to the primary font rather than to the system stack. */
function fontStackOrPrimary(family) {
    return family
        ? `"${family.name}", var(${PRIMARY_FONT_CSS_VAR})`
        : `var(${PRIMARY_FONT_CSS_VAR})`;
}
/**
 * Every custom property the storefront sets on `:root` for a brand, in the
 * order it has always emitted them. The single source for the storefront's
 * own `CSSVariableSetter` and for anything that renders brand HTML outside
 * the storefront — a section screenshot, say — so the two cannot drift.
 */
export function buildStorefrontCssVariables(settings, enableShopperBrandSettings = ENABLE_SHOPPER_BRAND_SETTINGS) {
    var _a, _b, _c;
    const pageTextColor = settings.primaryPageTextColor;
    const pageFillColor = settings.primaryPageFillColor;
    const tertiary = settings.tertiaryCtaTextColor && settings.tertiaryCtaFillColor
        ? {
            text: settings.tertiaryCtaTextColor,
            fill: settings.tertiaryCtaFillColor,
        }
        : null;
    const fonts = fontFamilyBySlot(settings);
    const entries = [
        [
            PRIMARY_FONT_CSS_VAR,
            `"${(_a = fonts.primary) === null || _a === void 0 ? void 0 : _a.name}", ${FONT_FAMILY_DEFAULTS}`,
        ],
        [
            SECONDARY_FONT_CSS_VAR,
            `"${(_b = fonts.secondary) === null || _b === void 0 ? void 0 : _b.name}", var(${PRIMARY_FONT_CSS_VAR})`,
        ],
        [FONT_3_CSS_VAR, fontStackOrPrimary(fonts.font3)],
        [FONT_4_CSS_VAR, fontStackOrPrimary(fonts.font4)],
        [FONT_5_CSS_VAR, fontStackOrPrimary(fonts.font5)],
        [PRIMARY_PAGE_TEXT_COLOR_VAR, pageTextColor],
        [PRIMARY_PAGE_FILL_COLOR_VAR, pageFillColor],
        [PRIMARY_CTA_TEXT_COLOR_VAR, settings.primaryCtaTextColor],
        [PRIMARY_CTA_FILL_COLOR_VAR, settings.primaryCtaFillColor],
        [ACCENT_FILL_COLOR_VAR, settings.accentFillColor],
        [ACCENT_TEXT_COLOR_VAR, settings.accentTextColor],
        [SECONDARY_CTA_TEXT_COLOR_VAR, settings.secondaryCtaTextColor],
        [SECONDARY_CTA_FILL_COLOR_VAR, settings.secondaryCtaFillColor],
        [TERTIARY_CTA_TEXT_COLOR_VAR, tertiary === null || tertiary === void 0 ? void 0 : tertiary.text],
        [TERTIARY_CTA_FILL_COLOR_VAR, tertiary === null || tertiary === void 0 ? void 0 : tertiary.fill],
        [PRIMARY_DISCOUNT_COLOR_VAR, settings.primaryDiscountColor],
        [SECONDARY_DISCOUNT_COLOR_VAR, settings.secondaryDiscountColor],
        [PRIMARY_ICON_COLOR_VAR, settings.iconColorOverride || pageTextColor],
        [
            SUBTLE_BORDER_COLOR_VAR,
            settings.borderColorOverride ||
                `color-mix(in srgb, ${pageTextColor} 10%, white 90%)`,
        ],
        [
            SUBTLE_PRIMARY_COLOR_VAR,
            `color-mix(in srgb, ${pageTextColor} 40%, white 60%)`,
        ],
        [HEADER_BORDER_COLOR_VAR, headerBorderColor(settings)],
        [
            PRIMARY_CTA_BORDER_VAR,
            computeCtaBorder(settings.primaryCtaFillColor, settings.primaryCtaTextColor, pageFillColor),
        ],
        [
            SECONDARY_CTA_BORDER_VAR,
            computeCtaBorder(settings.secondaryCtaFillColor, settings.secondaryCtaTextColor, pageFillColor),
        ],
        [
            TERTIARY_CTA_BORDER_VAR,
            tertiary
                ? computeCtaBorder(tertiary.fill, tertiary.text, pageFillColor)
                : null,
        ],
        [
            BUTTON_BORDER_RADIUS_VAR,
            BUTTON_BORDER_RADIUS_CSS[settings.buttonBorderRadiusStyle],
        ],
        ...(enableShopperBrandSettings
            ? [
                [
                    MEDIA_BORDER_RADIUS_VAR,
                    MEDIA_BORDER_RADIUS_CSS[(_c = settings.mediaBorderRadiusStyle) !== null && _c !== void 0 ? _c : 'ROUNDED'],
                ],
            ]
            : []),
        ...getTypographyCssVariableEntries(settings.typographyRoleOverrides),
    ];
    return entries.filter((entry) => Boolean(entry[1]));
}
/** The `:root { … }` block the storefront inlines server-side. */
export function buildStorefrontCssDeclarations(settings, enableShopperBrandSettings = ENABLE_SHOPPER_BRAND_SETTINGS) {
    const declarations = buildStorefrontCssVariables(settings, enableShopperBrandSettings)
        .map(([name, value]) => `${name}: ${value};`)
        .join('\n');
    return declarations ? `:root {\n${declarations}\n }` : '';
}
